import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import { GrnAttachmentsProps } from '../../features/grn/types';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  MenuItem,
  Select,
  Divider,
  IconButton,
  InputLabel,
  Menu,
  Collapse,
  Snackbar,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { MoreVert, Search as SearchIcon, AttachFile as AttachFileIcon } from "@mui/icons-material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ImagePopUp from "./grnpopup/ImagePopUp";
import {
  CON_STATUS_ACTIVE,
  CON_STATUS_DELETED,
} from "../../utils/sys/Constants";
import { externalgetApi, externalImagepostApi } from "../../services/externalApis";
import { SETTING_API_03, SETTING_API_09, UPLOAD_API } from "../../api/ApiModel";
import { useSelector } from "react-redux";
import Upload from "../common/Upload";
import { DeleteGrnDocsData, UpdateGrnDocsData } from "../../services/GrnApi";
import {
  MSG_GRN_DELETE,
  MSG_GRN_DELETE_FAILED,
} from "../../utils/sys/Messages";
import { isImageFile } from "../../utils/grn/fileUtils";
import { ENDPOINT } from "../../api/EndPoints";

const DEFAULT_DOC_CATEGORY = "GRN-Based Document Types";

/** Returns URL to use for attachment src: data URLs and absolute URLs as-is; relative paths get API base prepended. */
const getAttachmentDisplaySrc = (value: string): string => {
  if (!value || typeof value !== "string") return "";
  if (value.startsWith("data:") || value.startsWith("http://") || value.startsWith("https://")) return value;
  const base = ENDPOINT.replace(/\/api\/?$/, "");
  return base + (value.startsWith("/") ? value : "/" + value);
};

const GrnAttachments: React.FC<GrnAttachmentsProps> = (props) => {
  const {
    attachments,
    setAttachments,
    grncode = "",
    grndoccode = "",
    refreshGrnData = () => {},
    variant = "standard",
    docCategoryName = DEFAULT_DOC_CATEGORY,
    allowMissingDocumentType = false,
  } = props;
  const theme = useTheme();
  const styles = useStyles(theme);
  const [showAttachments, setShowAttachments] = useState(true);
  const [fileName, setFileName] = useState("No file selected");
  const [grnDocTypes, setGrnDocTypes] = useState([]);
  const [documentType, setDocumentType] = useState<any>("");
  const [description, setDescription] = useState("");
  const [doctypeothername, setDoctypeothername] = useState("");
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = React.useState<"error" | "warning" | "info" | "success">("error");
  const [alertMessage, setAlertMessage] = React.useState("");
  // Get user parameters from Redux store
  const SYS_USER = useSelector((state: any) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  const [attachmentFileName, setAttachmentFileName] = useState("");
  const [byteArray, setByteArray] = useState([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  const [file, setFile] = useState(null);
  const [fileBase64, setFileBase64] = useState("");

  // Ref to prevent duplicate API calls
  const hasFetchedDocCategoryRef = useRef(false);

const isResponseVariant = variant === "response";
const shouldRequireDocType = !(allowMissingDocumentType || isResponseVariant);

const addAttachmentWithData = (
  fileData: string,
  fileNameValue: string,
  rawFileValue: File | null,
  uploadedPath: string = ""
) => {
  console.log("🚀 DEBUG - addAttachmentWithData called with:");
  console.log("  fileNameValue:", fileNameValue);
  console.log("  rawFileValue:", rawFileValue);
  console.log("  rawFileValue?.name:", rawFileValue?.name);

  const docTypeCode =
    typeof documentType === "string"
      ? documentType
      : documentType?.doctypecode || "";

  // Removed validation - attachments are now optional
  // Only validate document type if it's required (not for response variant)
  if (shouldRequireDocType && !docTypeCode) {
    setAlertType("error");
    setAlertMessage("Please select a document type.");
    setAlertOpen(true);
    return;
  }

  // If no file data and no upload path, don't add attachment (but don't show error)
  if (!fileData && !uploadedPath) {
    return;
  }

  const selectedDoc = docTypeCode ? grnDocTypes.find(
    (doc) => doc.doctypecode === docTypeCode
  ) : null;

  const isOtherType =
    docTypeCode === "0" ||
    docTypeCode === "Other" ||
    (typeof documentType === "string" && documentType === "Other") ||
    documentType?.doctype === "Other";

  const resolvedDoctype = isResponseVariant
    ? ""
    : isOtherType && doctypeothername
      ? doctypeothername
      : selectedDoc?.doctype ||
        (typeof documentType === "object"
          ? documentType?.doctype || documentType?.doctypeothername
          : "") ||
        "";

  const fileExtension =
    (rawFileValue?.name || fileNameValue)?.split(".").pop()?.toLowerCase() || "";

  const finalFileName = isResponseVariant
    ? fileExtension || rawFileValue?.name || fileNameValue
    : rawFileValue?.name || fileNameValue;

  console.log("🚀 DEBUG - Creating attachment with:");
  console.log("  filename:", rawFileValue?.name || fileNameValue);
  console.log("  fileName:", finalFileName);
  console.log("  isResponseVariant:", isResponseVariant);

  // Determine file type at creation time
  const isImg = isImageFile(finalFileName);
  const isPdf = finalFileName.toLowerCase().endsWith(".pdf");
  let fileType: string;

  if (isPdf) {
    fileType = "pdf";
  } else if (isImg) {
    fileType = "image";
  } else {
    fileType = "attachment";
  }

  console.log("🚀 DEBUG - Determined fileType at creation:", fileType);

  // When upload succeeded, store only path (no base64) so state/localStorage stay small and navigation works (e.g. Firefox 2MB limit).
  const usePathOnly = Boolean(uploadedPath && uploadedPath.trim() !== "");
  const newAttachment = {
    doctypecode: docTypeCode || "0",
    doctype: resolvedDoctype,
    doctypeothername: isResponseVariant ? "" : (isOtherType ? doctypeothername : ""),
    remark: isResponseVariant ? "" : (description || ""),
    docremark: isResponseVariant ? "" : (description || ""),
    filename: uploadedPath?.trim() || "",
    fileName: finalFileName,
    fileType: fileType, // Store the calculated file type
    docpathjson: usePathOnly ? uploadedPath.trim() : fileData,
    bytarr: usePathOnly ? "" : fileData,
    fileUrl: uploadedPath?.trim() || "",
    FILEPATH: uploadedPath?.trim() || "",
    byteArray: usePathOnly ? [] : byteArray,
  };

  console.log("🚀 DEBUG - Final newAttachment object:", newAttachment);

  setAttachments((prev) => [...prev, newAttachment]);
  setDocumentType("");
  setDoctypeothername("");
  setDescription("");
  setFile(null);
  setFileName("No file selected");
  setAttachmentFileName("No file selected");
  setByteArray([]);
  setFileBase64("");
};

const handleAddAttachment = () => {
  addAttachmentWithData(fileBase64, attachmentFileName, file);
};


  const [attachAnchor, setAttachAnchor] = useState(null);

  const openAttachMenu = Boolean(attachAnchor);

  const handleAttachClick = (event) => {
    setAttachAnchor(event.currentTarget);
  };

  const handleAttachClose = () => {
    setAttachAnchor(null);
  };
  const toggleShowAttachments = () => {
    setShowAttachments((prev) => !prev);
  };

  const handleDeleteAttachment = (idOrIndex) => {
    // console.log("xxxxxx",idOrIndex);

    setAttachments((prev) => {
      if (typeof idOrIndex === "number") {
        // Delete by index for new attachments
        return prev.filter((_, idx) => idx !== idOrIndex);
      } else {
        // Delete by grndoccode for existing attachments
        const attachmentToDelete = prev.find(
          (att) => att.grndoccode === idOrIndex
        );
        if (attachmentToDelete && attachmentToDelete.grndoccode) {
          setDeletedAttachmentIds((ids) => [
            ...ids,
            attachmentToDelete.grndoccode,
          ]);
        }
        return prev.filter((att) => att.grndoccode !== idOrIndex);
      }
    });
    setDocumentType("");
    setDescription("");
    setFile(null);
    setFileName("No file selected");
    setAttachmentFileName("No file selected");
    setByteArray([]);
  };

  const handleDoctypeOtherNameChange = (index, value) => {
  setAttachments((prev) =>
    prev.map((att, i) =>
      i === index ? { ...att, doctypeothername: value } : att
    )
  );
};

  const fetchGrnDocType = useCallback(async (docCatCode: string | number): Promise<void> => {
    if (!COMPCODE || !STAFFMAPLOCODE) {
      console.warn("Cannot fetch GRN document types: COMPCODE or STAFFMAPLOCODE missing", { COMPCODE, STAFFMAPLOCODE });
      return;
    }

    try {
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        DOCCATCODE: docCatCode,
        STS: CON_STATUS_ACTIVE,
      };
      console.log("Fetching GRN document types with params:", params);
      const response = await externalgetApi(SETTING_API_03, "GET", params);
      const docTypes = Array.isArray(response) ? response : [];
      console.log("Fetched GRN document types:", docTypes);
      setGrnDocTypes(docTypes); // Save doc types to state
    } catch (error) {
      console.error("Error fetching GRN document types", error);
      setGrnDocTypes([]);
    }
  }, [COMPCODE, STAFFMAPLOCODE]);

  const fetchGrnDocCategory = useCallback(async (): Promise<void> => {
    // Prevent duplicate calls
    if (hasFetchedDocCategoryRef.current) {
      console.log("Document category already fetched, skipping...");
      return;
    }

    if (!COMPCODE || !STAFFMAPLOCODE) {
      console.warn("Cannot fetch GRN document category: COMPCODE or STAFFMAPLOCODE missing", { COMPCODE, STAFFMAPLOCODE });
      return;
    }

    try {
      const params = { STS: CON_STATUS_ACTIVE };
      console.log("Fetching GRN document category with params:", params);
      console.log("Looking for category:", docCategoryName);
      const response = await externalgetApi(SETTING_API_09, "GET", params);
      console.log("Fetched document categories:", response);
      
      if (Array.isArray(response)) {
        // Try exact match first
        let grnCategory = response.find(
          (cat: any) => cat.doccatname === docCategoryName
        );
        
        // If not found, try case-insensitive and trimmed match
        if (!grnCategory) {
          grnCategory = response.find((cat: any) => 
            String(cat.doccatname || "").trim().toLowerCase() === String(docCategoryName || "").trim().toLowerCase()
          );
        }
        
        console.log("Found category:", grnCategory);
        
        if (grnCategory && grnCategory.doccatcode) {
          console.log("Fetching document types for category code:", grnCategory.doccatcode);
          await fetchGrnDocType(grnCategory.doccatcode);
          // Only mark as fetched if we successfully found the category and fetched doc types
          hasFetchedDocCategoryRef.current = true;
        } else {
          console.warn(`${docCategoryName} category not found. Available categories:`, response.map((cat: any) => cat.doccatname));
          setGrnDocTypes([]);
          // Don't mark as fetched if category not found - allow retry
        }
      } else {
        console.warn("Invalid response format for document categories:", response);
        setGrnDocTypes([]);
        // Don't mark as fetched if response is invalid - allow retry
      }
    } catch (error) {
      console.error("Error fetching GRN document category", error);
      hasFetchedDocCategoryRef.current = false; // Reset on error to allow retry
      setGrnDocTypes([]);
    }
  }, [fetchGrnDocType, docCategoryName, COMPCODE, STAFFMAPLOCODE]);

  // Initial fetch - when component mounts or docCategoryName changes
  useEffect(() => {
    // Reset ref when docCategoryName changes to allow re-fetch
    hasFetchedDocCategoryRef.current = false;
    
    if (COMPCODE && STAFFMAPLOCODE) {
      console.log("Initializing GRN Attachments - fetching document types...", { COMPCODE, STAFFMAPLOCODE, docCategoryName });
      fetchGrnDocCategory();
    } else {
      console.warn("Cannot initialize GRN Attachments: COMPCODE or STAFFMAPLOCODE missing", { COMPCODE, STAFFMAPLOCODE });
    }
  }, [fetchGrnDocCategory, COMPCODE, STAFFMAPLOCODE, docCategoryName]);

  const handleDeleteDocsData = (grndoccodeToDelete) => {
    DeleteGrnDocsData({
      attachments,
      USERNAME,
      COMPCODE,
      grncode,
      grndoccode: grndoccodeToDelete,
      sts: CON_STATUS_DELETED,

      onSuccess: (response) => {
        handleDeleteAttachment(grndoccodeToDelete);
        setAlertType("success");
        setAlertMessage(MSG_GRN_DELETE);
        if (typeof refreshGrnData === "function") {
          refreshGrnData();
        }
        setAlertOpen(true);
        // window.location.reload();
        // goNextToView("success");
      },
      onError: (error) => {
        console.error("Failed to create GRN:", error);
        setAlertType("error");
        setAlertMessage(MSG_GRN_DELETE_FAILED);
        setAlertOpen(true);
      },
    });
  };

  return (
    <>
      <Box sx={styles.box_space_between} onClick={toggleShowAttachments}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            
          }}
        >
          <IconButton
            sx={{
              transform: showAttachments ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
              mr: 1,
            }}
          >
            <ExpandMoreOutlinedIcon />
          </IconButton>
          <Typography sx={styles.grn_sub_title_text}>Attachments</Typography>
        </Box>
      </Box>

      <Divider />
      <Collapse in={showAttachments} timeout="auto" unmountOnExit>
        <Grid container spacing={2} pt={2}>
{!isResponseVariant && (
  <>
  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
  <FormControl fullWidth size="small">
    <InputLabel>Document Type</InputLabel>
    <Select
      label="Document Type"
      value={documentType?.doctypecode || ""} //  must match MenuItem value
      onChange={(e) => {
        const selected = grnDocTypes.find(
          (doc) => doc.doctypecode === e.target.value
        );
        setDocumentType(selected || { doctypecode: "", doctype: "" });
        if (selected?.doctype !== "Other") setDoctypeothername(""); // reset if not Other
      }}
      MenuProps={{
        PaperProps: { sx: { boxShadow: 2 } },
      }}
    >
      {grnDocTypes.map((doc) => (
        <MenuItem key={doc.doctypecode} value={doc.doctypecode}>
          {doc.doctype}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>

{documentType?.doctype === "Other" && (
 <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
    <TextField
      fullWidth
      label="Document Name"
      size="small"
      value={doctypeothername || ""}
      onChange={(e) => setDoctypeothername(e.target.value)}
      inputProps={{ maxLength: 15 }}
      error={(doctypeothername || "").length >= 15}
      helperText={
        (doctypeothername || "").length >= 15
          ? "Maximum 15 characters allowed"
          : ""
      }
      FormHelperTextProps={{
        sx: { fontSize: "12px", color: "red" },
      }}
    />
  </Grid>
)}

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
         <TextField
  fullWidth
  label="Description"
  onChange={(e) => setDescription(e.target.value)}
  size="small"
  value={description}
  inputProps={{ maxLength: 30 }}
  error={description.length >= 30}
  helperText={
    description.length >= 30
      ? "Maximum 30 characters allowed"
      : ""
  }
  FormHelperTextProps={{
    sx: { fontSize: "12px", color: description.length > 30 ? "red" : "text.secondary" },
  }}
/>

          </Grid>
  </>
)}

          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box display="flex" alignItems="center" gap={1} pb={1}>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "12px",
                  fontWeight: 450,
                  lineHeight: "auto",
                }}
              >
               Attach Documents
              </Typography>

              {/* <Typography
                sx={{
                  color: (theme.palette as any).greyscale.white,
                  fontSize: "12px",
                  fontWeight: 450,
                  lineHeight: "auto",
                  bgcolor: "primary.main",
                  px: 1,
                  borderRadius: "50px",
                }}
              >
                3
              </Typography> */}
            </Box>
            <Upload
              MAX_FILESIZE={10 * 1024 * 1024}
              setSnackbarMessage={setAlertMessage}
              setOpenErrorSnackbar={setAlertOpen}
              setFilePath={() => {}}
              onFileSelect={async (fileName, base64DataURL, rawFile) => {
                setFile(rawFile);
                setAttachmentFileName(fileName);

                try {
                  const base64Only = base64DataURL.split(",")[1]; // Extract base64 only
                  let uploadedPath = "";

                  if (rawFile) {
                    const formData = new FormData();
                    formData.append("file", rawFile);
                    const response = await externalImagepostApi(UPLOAD_API, formData);
                    uploadedPath =
                      response?.filepath ||
                      response?.filePath ||
                      response?.FILEPATH ||
                      "";
                  }

                  setFileBase64(base64Only);
                  addAttachmentWithData(base64Only, fileName, rawFile, uploadedPath);
                  console.log("Attachment uploaded:", fileName, "uploadedPath:", uploadedPath);
                } catch (error) {
                  console.error("Error uploading attachment", error);
                  setAlertType("error");
                  setAlertMessage("Failed to upload attachment.");
                  setAlertOpen(true);
                }
              }}
              fileName={attachmentFileName}
              setFileName={setAttachmentFileName}
              objectUrl=""
              resetKey={attachments.length}
            />
            
          </Grid>
        </Grid>

        <Box
          sx={{
            borderTop: `1px solid ${(theme.palette as any).greyscale.stroke_1}`,
            mb: 10,
            mt: 1,
            pt: 1,
            gap: 2,
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          {attachments.map((attachment, index) => {
            let images = [];

            // First, determine the fileName from the attachment
            const attachmentFileName =
              attachment.fileName || attachment.filename || attachment.FILENAME || "";
            console.log("🔍 DEBUG - Processing attachment #" + index + ", fileName:", attachmentFileName);

            try {
              // Try parse as JSON array (existing attachments from server)
              images = attachment.docpathjson
                ? JSON.parse(attachment.docpathjson)
                : [];

              console.log("🔍 DEBUG - Successfully parsed as JSON, images:", images);

              // Normalize TYPE for existing attachments to ensure correct display
              images = images.map((img) => {
                const fileName = img.FILENAME || attachmentFileName;
                const isImg = isImageFile(fileName);
                const isPdf = fileName.toLowerCase().endsWith(".pdf");

                // Determine the correct TYPE based on file extension
                let correctType;
                if (isPdf) {
                  correctType = "pdf";
                } else if (isImg) {
                  correctType = "image";
                } else {
                  // For unknown types, check if there's any file extension
                  correctType = "attachment"; // Generic attachment icon
                }

                console.log("🔍 DEBUG - Normalized existing attachment, fileName:", fileName, "TYPE:", correctType);

                // Always recalculate TYPE to ensure correctness
                return {
                  ...img,
                  TYPE: correctType,
                  FILENAME: fileName,
                };
              });
            } catch (error) {
              console.log("🔍 DEBUG - Failed to parse as JSON (new attachment), error:", error);
              // If parse fails, treat as base64 string (new attachment)
              if (attachment.docpathjson || attachment.FILEPATH || attachment.fileUrl) {
                // Check if fileType was already calculated at creation time
                let fileType = (attachment as any).fileType;

                console.log("🔍 DEBUG - Attachment fileType from object:", fileType);
                console.log("🔍 DEBUG - Full attachment object:", attachment);

                // If fileType wasn't set (old attachment from localStorage), calculate it
                if (!fileType) {
                  const fileName = attachment.fileName || attachment.filename || "";
                  console.log("🔍 DEBUG - Calculating fileType from fileName:", fileName);

                  const isImg = isImageFile(fileName);
                  const isPdf = fileName.toLowerCase().endsWith(".pdf");

                  console.log("🔍 DEBUG - isImageFile result:", isImg);
                  console.log("🔍 DEBUG - isPdf result:", isPdf);

                  if (isPdf) {
                    fileType = "pdf";
                  } else if (isImg) {
                    fileType = "image";
                  } else {
                    fileType = "attachment";
                  }

                  console.log("🔍 DEBUG - Calculated fileType:", fileType);
                }

                const fileName =
                  attachment.fileName ||
                  attachment.filename ||
                  attachment.FILENAME ||
                  "";

                const fileSource =
                  attachment.docpathjson || attachment.FILEPATH || attachment.fileUrl;
                const isDataOrAbsoluteUrl =
                  typeof fileSource === "string" &&
                  (fileSource.startsWith("data:") || fileSource.startsWith("http://") || fileSource.startsWith("https://"));
                const isBase64 =
                  typeof fileSource === "string" &&
                  fileSource.length > 100 &&
                  /^[A-Za-z0-9+/=]+$/.test(fileSource);
                // Determine mime prefix based on fileType (for base64 only)
                let mimePrefix;
                if (fileType === "pdf") {
                  mimePrefix = "data:application/pdf;base64,";
                } else if (fileType === "image") {
                  mimePrefix = "data:image/*;base64,";
                } else {
                  mimePrefix = "data:application/octet-stream;base64,";
                }

                console.log("🔍 DEBUG - Final fileType for rendering:", fileType);

                const displaySrc = isDataOrAbsoluteUrl
                  ? fileSource
                  : isBase64
                    ? `${mimePrefix}${fileSource}`
                    : getAttachmentDisplaySrc(fileSource || "");

                const fileData = {
                  FILEPATH: displaySrc,
                  REMARK: attachment.remark || attachment.docremark || "",
                  TYPE: fileType,
                  FILENAME: fileName,
                };
                images = [fileData];
              }
            }

            if (images.length === 0 && attachmentFileName) {
              const fallbackFileType =
                (attachment as any).fileType ||
                (attachmentFileName.toLowerCase().endsWith(".pdf")
                  ? "pdf"
                  : isImageFile(attachmentFileName)
                  ? "image"
                  : "attachment");
              const fallbackSource =
                attachment.fileUrl || attachment.FILEPATH || attachment.docpathjson || "";
              const isDataOrAbsoluteFallback =
                typeof fallbackSource === "string" &&
                (fallbackSource.startsWith("data:") || fallbackSource.startsWith("http://") || fallbackSource.startsWith("https://"));
              const isBase64Fallback =
                typeof fallbackSource === "string" &&
                fallbackSource.length > 100 &&
                /^[A-Za-z0-9+/=]+$/.test(fallbackSource);
              const fallbackMimePrefix =
                fallbackFileType === "pdf"
                  ? "data:application/pdf;base64,"
                  : fallbackFileType === "image"
                  ? "data:image/*;base64,"
                  : "data:application/octet-stream;base64,";

              const fallbackDisplaySrc = isDataOrAbsoluteFallback
                ? fallbackSource
                : isBase64Fallback
                  ? `${fallbackMimePrefix}${fallbackSource}`
                  : getAttachmentDisplaySrc(fallbackSource);

              images = [
                {
                  FILEPATH: fallbackDisplaySrc,
                  REMARK: attachment.remark || attachment.docremark || "",
                  TYPE: fallbackFileType,
                  FILENAME: attachmentFileName,
                },
              ];
            }

            return (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                gap={2}
                p={1}
                bgcolor="background.paper"
                borderRadius={1}
                sx={{ boxShadow: `0px 1px 2px rgba(0, 0, 0, 0.1)` }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  {/* {images.map((img, i) => (
                    <img
                      key={i}
                      src={img.FILEPATH}
                      alt={img.REMARK || `Attachment Image ${i + 1}`}
                      style={{ width: 40, height: 40, cursor: "pointer" }}
                      onClick={(e) => {
                        setSelectedImageData(img);
                        setAttachAnchor(e.currentTarget);
                      }}
                    />
                  ))} */}
                  {images.map((img, i) => (
                    <Box key={i} display="flex" alignItems="center" gap={1}>
                      {img.TYPE === "pdf" ? (
                        <Box
                          onClick={() => {
                            setSelectedImageData(img);
                            setImagePopupOpen(true);
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f0f0f0",
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                        >
                          <Typography fontSize="10px" textAlign="center">
                            PDF
                          </Typography>
                        </Box>
                      ) : img.TYPE === "image" ? (
                        <img
                          src={img.FILEPATH}
                          alt="File"
                          style={{ width: 40, height: 40, cursor: "pointer", objectFit: "cover" }}
                          onClick={() => {
                            setSelectedImageData(img);
                            setImagePopupOpen(true);
                          }}
                        />
                      ) : (
                        <Box
                          onClick={() => {
                            setSelectedImageData(img);
                            setImagePopupOpen(true);
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#e0e0e0",
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                        >
                          <AttachFileIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                        </Box>
                      )}

                      <IconButton
                        onClick={(e) => {
                          setSelectedImageData(img);
                          handleAttachClick(e);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
                <Menu
                  anchorEl={attachAnchor}
                  open={openAttachMenu}
                  onClose={handleAttachClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.15)",
                      },
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setImagePopupOpen(true);
                      handleAttachClose();
                    }}
                  >
                    View
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      if (attachment.grndoccode) {
                        // Delete saved attachment (existing)
                        handleDeleteDocsData(attachment.grndoccode);

                        handleDeleteAttachment(attachment.grndoccode);
                      } else {
                        // Delete new (unsaved) attachment
                        handleDeleteAttachment(index);
                      }

                      handleAttachClose(); // close the menu
                    }}
                  >
                    Delete
                  </MenuItem>
                </Menu>

                <ImagePopUp
                  open={imagePopupOpen}
                  handleClose={() => setImagePopupOpen(false)}
                  image={selectedImageData?.FILEPATH}
                  name={selectedImageData?.FILENAME}
                  description={selectedImageData?.REMARK}
                />
              </Box>
            );
          })}
        </Box>
      </Collapse>
      <Snackbar
        open={alertOpen}
        autoHideDuration={3000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertType}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GrnAttachments;
